import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { useLanguage } from '@/context/LanguageContext'
import { CreateInvoiceReq, ProductType } from '@/services/mobi-api/invoice/types'
import useIntl from '@/utils/intl'
import { Checkbox, Dialog, TextField } from '@mui/material'
import React, { FunctionComponent, useEffect, useReducer } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { useMobiApi } from '@/services/mobi-api'
import { useToast } from '@/context/ToastContext'
import { SimRecover, SimRecoverRes } from '@/services/mobi-api/sim/types'
import Loader from '@/components/ui/loader'
import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import MCButton from '@/components/ui/Buttons/MobicomButton'
import { Close } from '@mui/icons-material'
import { checkEmail, isMobile } from '@/utils/helpers'
import Image from 'next/image'
import { resolveImageUrl } from '@/utils/constants'
import { getSystemEnv } from '@/utils/env'
import { useAuth } from '@/context/AuthContext'
import RichText from '@/components/ui/RichText'
import { useCmsApi } from '@/services/cms-api'
import { useTheme } from '@/context/ThemeContext'

interface DigitalConsentData {
  titleMn: string
  titleEn: string
  bodyMn: string
  bodyEn: string
}
interface State {
  email?: string
  corpId?: string
  checked?: boolean
  vatType?: string
  hasPrice?: boolean
  loading?: boolean
  request?: SimRecover
  openTerms?: boolean
  instanceID?: string
  showConsent?: boolean
  consentSession?: string
  termsAgreed?: boolean
  digitalConsent?: DigitalConsentData
}
const Step5: FunctionComponent = () => {
  const router = useRouter()
  const { isWebView, isMonPay } = useAuth()
  const { locale } = useLanguage()
  const { getDigitalConsentData } = useCmsApi()
  const { sim, invoice } = useMobiApi()
  const { showError } = useToast()
  const searchParams = useSearchParams()
  const { theme } = useTheme()

  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      email: '',
      corpId: '',
      checked: false,
      vatType: 'CONSUMER',
      loading: true,
      openTerms: false,
      termsAgreed: false
    })
  const { translate } = useIntl()

  useEffect(() => {
    handleQuery()
  }, [searchParams])

  const handleQuery = (): void => {
    const _id = searchParams.get('_id')
    if (_id !== null) {
      setState({ instanceID: _id })
      void fetchRequest(_id)
    } else {
      showError(translate('error'))
      router.push('/')
    }
  }
  const closeModal = (): void => {
    setState({ checked: true, openTerms: false })
  }
  const fetchRequest = async (_id: string): Promise<void> => {
    try {
      const res = await sim.get(_id)
      if (res !== undefined) {
        setState({ request: res })
        if (res.price !== undefined && res.price > 0) {
          setState({ hasPrice: true })
        }
        void fetchConsentReq(res)
      } else {
        showError('Dan validation failed')
        router.push('/')
      }
    } catch (e) {
      console.error(e)
      showError(translate('error'))
    }
  }

  const fetchConsentReq = async (res: SimRecoverRes): Promise<void> => {
    try {
      const response = await sim.checkConsent(res.isdn)
      if (response !== undefined) {
        if (response.privacyRequired) {
          await fetchConsentText()
          setState({ showConsent: true, consentSession: response.sessionId })
        }
      } else {
        showError('failed')
        router.push('/')
      }
    } catch (e) {
      console.error(e)
      showError(translate('error'))
    } finally {
      setState({ loading: false })
    }
  }
  const fetchConsentText = async (): Promise<void> => {
    try {
      const res = await getDigitalConsentData()
      setState({ digitalConsent: res })
    } catch (e) {
      console.error(e)
      showError(translate('error'))
    }
  }

  const createConsent = async (): Promise<void> => {
    try {
      const response = await sim.createConsent(state.consentSession ?? '')
      if (response?.success !== undefined && response?.success) {
        setState({ showConsent: false })
      } else {
        showError('failed')
        router.push('/')
      }
    } catch (e) {
      console.error(e)
      showError(translate('error'))
    } finally {
      setState({ loading: false })
    }
  }

  const VAT = [
    {
      name: translate('shop.vatCons'),
      icon: '/uploads/icons/user1.svg',
      darkIcon: '/uploads/icons/user.svg',
      type: 'CONSUMER'
    },
    {
      name: translate('shop.vatCorp'),
      icon: '/uploads/icons/briefcase.svg',
      darkIcon: '/uploads/icons/briefcase1.svg',
      type: 'CORPORATE'
    }
  ]

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setState({ checked: event.target.checked })
  }
  const handleChangeVatType = (value: string): void => {
    setState({ vatType: value })
  }
  const handleContinue = async (): Promise<void> => {
    const _id = searchParams.get('_id')

    if (_id === null) {
      showError(translate('error'))
      return
    }

    const payload: CreateInvoiceReq = {
      isdn: state.request?.isdn,
      products: [{ productType: ProductType.ESIM, price: state.request?.price, quantity: 1, productCode: _id }],
      customerType: state.vatType === 'CORPORATE' ? 'CORPORATE' : 'CONSUMER',
      languageCode: locale,
      vatEmail: state.email,
      corpId: state.vatType === 'CORPORATE' ? state.corpId : undefined,
      device: isMobile(),
      redirectUrl: getSystemEnv().DOMAIN + '/paymentSuccess?simRecoveryId=' + (state.instanceID ?? ''),
    }
    try {
      const res = await invoice.create(payload)
      if (isWebView && !isMonPay) {
        const data = {
          key: 'invoice',
          value: {
            command: 'esim.recovery',
            identifier: state.request?.isdn,
            product: 'ESIM.RECOVERY',
            email: state.email,
            amount: state.request?.price,
            register: state.vatType === 'CORPORATE' ? state.corpId : '',
            vatType: state.vatType === 'CORPORATE' ? 'CORP' : 'CONS',
            activationCode: res?.invoiceId,
            productName: translate('quick.simRecover')
          }
        }
        let webView
        if ((window as any).mobiBridge !== undefined) {
          webView = (window as any).mobiBridge
        }
        if ((window as any).ReactNativeWebview !== undefined) {
          webView = (window as any).ReactNativeWebview
        }
        if (webView !== undefined) {
          webView.postMessage(JSON.stringify(data))
        }
      }
      else if (res?.redirectUrl !== undefined && res.redirectUrl !== '') {
        router.push(res.redirectUrl)
      } else {
        showError(translate('error'))
      }
    } catch (e) {
      console.error(e)
    }
  }
  const disableContinue = (): boolean => {
    let check = false
    if (!checkEmail(state.email ?? '')) { check = true }
    if (state.vatType === 'CORPORATE' && (state.corpId?.length ?? 0) < 7) { check = true }
    if (state.checked !== true) { check = true }
    if (state.vatType === '') { check = true }
    return check
  }

  if (state.loading === true) {
    return <div className='w-full h-96 flex items-center justify-center'><Loader /></div>
  }
  if (state.showConsent === true) {
    return (
      <div className='rounded-2xl bg-surfaceInverse h-full md:h-[80vh] overflow-auto relative'>
        <div className='fixed md:sticky top-0 right-0 left-0 z-50 p-6 bg-surfaceInverse border-b border-secondarySoftHigh'>
          <h1 className='text-center text-textHigh font-extrabold text-lg'>{locale === 'en' ? (state.digitalConsent?.titleEn ?? '') : (state.digitalConsent?.titleMn ?? '')}</h1>
        </div>
        <div className='p-8 pb-36 md:pb-8'>
          <RichText content={locale === 'en' ? (state.digitalConsent?.bodyEn ?? '') : (state.digitalConsent?.bodyMn ?? '')} />
        </div>
        <div className='fixed md:sticky bottom-0 right-0 left-0 z-50 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
          <div className='flex flex-row items-center mb-2' onClick={() => setState({ termsAgreed: state.termsAgreed === false })}>
            <Checkbox color='error' className='text-textMid' checked={state.termsAgreed} />
            <div>
              <p className='text-sm font-medium text-textHigh'>{translate('acceptTermsOfService')}</p>
            </div>
          </div>
          <DarkButton fullwidth disabled={state.termsAgreed === undefined || !state.termsAgreed} onClick={() => { void createConsent() }}>{translate('continue')}</DarkButton>
        </div>
      </div>
    )
  } else {
    if (state.hasPrice === true) {
      return (
        <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-between overflow-auto'>
          <div className='flex-grow overflow-auto'>
            <div className='md:p-8 p-4 flex flex-col gap-4 border-b border-secondaryMidStroke'>
              <span className='text-textMid text-xs md:text-sm font-bold'>{translate('order.summary')}</span>
              <div className='py-2 bg-surfaceWebGrey border border-secondaryMidStroke w-full rounded-xl'>
                <div className='flex justify-between px-4 py-2'>
                  <span className='text-textMid text-xs md:text-sm font-medium'>{translate('phoneNumber')}:</span>
                  <span className='text-textHigh text-xs md:text-sm font-bold'>{state.request?.isdn}</span>
                </div>
                <div className='flex justify-between px-4 py-2'>
                  <span className='text-textMid text-xs md:text-sm font-medium'>{translate('product')}:</span>
                  <span className='text-textHigh text-xs md:text-sm font-bold'>{state.request?.simtype}</span>
                </div>
                <div className='flex justify-between px-4 py-2'>
                  <span className='text-textMid text-xs md:text-sm font-medium'>{translate('price')}:</span>
                  <span className='text-textHigh text-xs md:text-sm font-bold'>{state.request?.price}₮</span>
                </div>
              </div>
            </div>
            <div className='md:p-8 p-4'>
              <span className='text-textMid text-xs md:text-sm font-bold'>{translate('shop.vat')}</span>
              <div className='flex gap-4 mt-4 w-full  md:w-96'>
                {VAT.map((vat, index) => (
                  <MCButton selected={vat.type === state.vatType} type='shop' fullwidth size='sm' onClick={() => handleChangeVatType(vat.type)} key={index} leadingIconImg={theme === 'dark' ? resolveImageUrl(vat.darkIcon) : resolveImageUrl(vat.icon)}>{vat.name}</MCButton>
                ))}
              </div>
              <div className='md:w-96 w-full'>
                <TextField
                  id='outlined-basic'
                  label={state.vatType === 'CORPORATE' ? translate('shop.corpEmail') : translate('shop.vatEmail')}
                  variant='outlined'
                  className='w-full rounded-lg md:mt-8 mt-4 border-secondaryHighStroke bg-surfaceInverse text-textMid'
                  onChange={(e) => { setState({ email: e.target.value }) }}
                  InputLabelProps={{
                    style: { color: 'var(--color-text-textHigh)' }
                  }}
                  InputProps={{
                    style: {
                      color: 'var(--color-text-textHigh)',
                      '&::placeholder': {
                        color: 'var(--color-text-secondaryHighStroke)'
                      }
                    } as any
                  }}
                  sx={{
                    '& fieldset': {
                      borderRadius: '8px'
                    }
                  }}
                />
                {
                  state.vatType === 'CORPORATE'
                    ? (
                      <TextField
                        type='string'
                        id='outlined-basic'
                        value={state.corpId}
                        label={translate('shop.vatCorpId')}
                        variant='outlined'
                        className='w-full rounded-lg md:mt-8 mt-4 border-secondaryHighStroke bg-surfaceInverse text-textMid'
                        onChange={(e) => {
                          setState({ corpId: e.target.value })
                        }}
                        InputLabelProps={{
                          style: { color: 'var(--color-text-textHigh)' }
                        }}
                        InputProps={{
                          style: {
                            color: 'var(--color-text-textHigh)',
                            '&::placeholder': {
                              color: 'var(--color-text-secondaryHighStroke)'
                            }
                          } as any
                        }}
                        sx={{
                          '& fieldset': {
                            borderRadius: '8px'
                          }
                        }}
                      />
                    )
                    : <></>
                }
              </div>
              <div className='flex items-center mt-10'>
                <Checkbox color='error' checked={state.checked} onChange={handleChange} inputProps={{ 'aria-label': 'controlled' }} className='cursor-pointer text-textHigh' />
                <p className='text-xs md:text-sm font-medium cursor-pointer hover:underline hover:underline-offset-4 hover:text-[#EF4444] text-textHigh' onClick={() => setState({ openTerms: true })}>{translate('acceptTermsOfService')}</p>
              </div>
            </div>
          </div>
          <div className='fixed md:sticky bottom-0 right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
            <DarkButton fullwidth disabled={disableContinue()} onClick={() => { void handleContinue() }}>{translate('continue')}</DarkButton>
          </div>
          <Dialog open={state.openTerms ?? false}>
            <div className='p-4'>
              <div className='flex justify-between items-center w-full'>
                <span className='text-textMid font-bold text-lg'>{translate('termsOfService')}</span>
                <DarkIconButton type='text' size='sm' icon={<Close fontSize='small' />} onClick={() => setState({ openTerms: true })} />
              </div>
              <div className='py-8 px-10 border border-slate-200 rounded-lg mt-4'>
                <p className='text-lg font-bold text-textHigh'>{translate('generalProvs')}</p>
                <p className='text-sm text-textMid font-medium text-justify mt-6'>{translate('termMain')}
                  <br />1. {translate('term1')}
                  <br />2. {translate('term2')}
                </p>
              </div>
              <div className='mt-4 w-full flex justify-end'>
                <MCButton size='sm' onClick={closeModal}>{translate('accept')}</MCButton>
              </div>
            </div>
          </Dialog>
        </div>
      )
    } else {
      return (
        <div className='border border-[#4ADE80] rounded-lg flex justify-center items-center lg:p-10 p-6 xl:w-fit md:w-1/2'>
          <Image draggable={false} width={60} height={60} alt='img' src={resolveImageUrl('/uploads/icons/payment-success.png')} className='xl:w-16 w-12' />
          <h1 className='text-textHigh xl:text-2xl text-lg font-bold mt-4'>{translate('success')}!</h1>
          <p className='text-textMid xl:text-sm text-xs font-medium mt-4'>{translate('thankyoutext')}</p>
          {
            state.request?.simtype === 'ESIM' && (
              <p className='text-textMid text-xs xl:text-sm font-bold 2xl:w-[500px] xl:w-[400px] mt-8 text-center'>{translate('sim.qrsent').replace('$email', state.request?.email)}</p>
            )
          }
        </div>
      )
    }
  }
}
export default Step5
